import React, { useState, useEffect } from "react";
import { Box, Grid, IconButton, Tooltip } from "@mui/material";
import { getFormattedDateTime, useToast } from "../../services/utils";
import FlexBetween from "../../components/FlexBetween";
import DataTable from "../../components/DataGrids/DataGrid";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate, useSearchParams } from "react-router-dom";
import ButtonComponent from "../../components/Button.Component";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import { deleteRole, reactivateRole } from "../../services/roleService";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import {
  IconEditBase,
  IconDeleteBlueSvg,
  IconEyeBase,
  IconAssignPermissionsBase,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
  IconReactivateSvg,
} from "../../common/utils/icons";
import usePaginatedFetchRolesList from "../../hooks/usePaginatedFetchRolesList";
import useDebounce from "../../hooks/useDebounce";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import { enableRowGroupByColumnProps } from "../../components/DataGridProps/DataGridProps";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import Loader from "../../components/Molecules/Loader/Loader";
import ConfirmDailog from "../../components/Dialogs/ConfirmDailog";
import { AssignPermissionsModal } from "../../components/Roles";
import { useAuth } from "../../contexts/AuthContext";
import { isAdminRole } from "../../types/role";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
import { useAppTranslation } from "../../hooks/useAppTranslation";
import { getConfig } from "../../config/runtimeConfig";

// Centralized Filter Configuration for Roles List
const ROLES_FILTER_CONFIG = {
  // Default filter state - single source of truth
  DEFAULT_STATE: {
    role_name: "",
    status_filter: "Active",
  },

  // Fields to persist in URL
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },

  // Field type mappings for URL serialization/deserialization
  FIELD_TYPES: {
    ID_FIELDS: [],
    STRING_FIELDS: ["role_name", "status_filter"],
    DATE_FIELDS: [],
  },
} as const;

const Roles: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const { t } = useAppTranslation();
  const defaultPage = getConfig("REACT_APP_DEFAULT_PAGE", "1");
  const { rowsPerPage } = useRowsPerPage();

  // Permission checks for role actions
  const canViewRole = hasPermission("user_role", "view");
  const canAddRole = hasPermission("user_role", "add");
  const canEditRole = hasPermission("user_role", "edit");
  const canDeleteRole = hasPermission("user_role", "delete");
  const canAssignPermissions = hasPermission("casbin_policies", "view");

  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // SearchFilterBar state - initialize from URL parameters
  const [searchFilterData, setSearchFilterData] = useState<any>(() => {
    const initialState: any = {
      ...ROLES_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Restore filters from URL parameters
    ROLES_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        initialState[field] = urlValue;
      }
    });

    return initialState;
  });

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [roleToDelete, setRoleToDelete] = useState<any>(null);
  const [permissionsModalOpen, setPermissionsModalOpen] =
    useState<boolean>(false);
  const [selectedRoleForPermissions, setSelectedRoleForPermissions] =
    useState<any>(null);

  // Debounce role_name with 400ms delay
  const debouncedRoleName = useDebounce(searchFilterData.role_name);

  // Determine if we should include role_name in API call
  const effectiveRoleName =
    debouncedRoleName.trim().length >= 3 ? debouncedRoleName : "";

  // Determine status filter value
  const effectiveStatusFilter = searchFilterData.status_filter;

  // Convert status_filter to isActive parameter
  let isActiveParam = "true"; // Default to active
  if (effectiveStatusFilter === "Inactive") {
    isActiveParam = "false";
  } else if (effectiveStatusFilter === "All") {
    isActiveParam = "all";
  }

  const [rolesData, refetchRoles] = usePaginatedFetchRolesList({
    setIsLoading,
    page,
    pSize,
    searchTerm: effectiveRoleName,
    isActive: isActiveParam,
  });

  // Sync filters to URL parameters (state → URL)
  useEffect(() => {
    const newParams = new URLSearchParams();

    ROLES_FILTER_CONFIG.URL_FIELDS.forEach((field) => {
      const value = searchFilterData[field];
      if (value !== null && value !== undefined && value !== "") {
        // Don't add default values to URL to keep it clean
        if (field === "status_filter" && value === "Active") {
          return; // Skip default status filter
        }
        newParams.set(field, String(value));
      }
    });

    // Only update URL if params have changed
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();
    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams, { replace: true });
    }
  }, [searchFilterData, searchParams, setSearchParams]);

  // Update filter state when URL changes (URL → state for back/forward navigation)
  useEffect(() => {
    const urlFilterState: any = {
      ...ROLES_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Extract filter parameters from URL
    ROLES_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        urlFilterState[field] = urlValue;
      }
    });

    // Check if URL filter state differs from current filter state
    const hasFilterChanges = Object.keys(urlFilterState).some((key) => {
      const urlValue = urlFilterState[key];
      const currentValue = searchFilterData[key];

      // Handle null/undefined/empty string comparisons
      const normalizedUrlValue =
        urlValue === null || urlValue === undefined || urlValue === ""
          ? ""
          : urlValue;
      const normalizedCurrentValue =
        currentValue === null ||
        currentValue === undefined ||
        currentValue === ""
          ? ""
          : currentValue;

      return normalizedUrlValue !== normalizedCurrentValue;
    });

    // Only update state if there are actual changes to prevent infinite loops
    if (hasFilterChanges) {
      setSearchFilterData(urlFilterState);
    }
  }, [searchParams]); // Only depend on searchParams to detect URL changes

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [effectiveRoleName, effectiveStatusFilter]);

  // Handle reactivate role
  const handleReactivate = async (role: any) => {
    try {
      setIsLoading(true);
      await reactivateRole(role.id);
      showToast("Role reactivated successfully", "success");
      // Refetch the roles list to update the UI
      refetchRoles();
    } catch (error: any) {
      console.error("Error reactivating role:", error);
      showToast(
        error?.response?.data?.detail || t("roles.toast.failedReactivate"),
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete role
  const handleDeleteRole = (role: any) => {
    // Check if role is admin type and prevent deletion
    if (isAdminRole(role)) {
      showToast(t("roles.toast.adminCannotDelete"), "error");
      return;
    }
    setRoleToDelete(role);
    setDeleteConfirmOpen(true);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (!roleToDelete) return;

    try {
      setIsLoading(true);
      await deleteRole(roleToDelete.id);
      showToast(t("roles.toast.deleted"), "success");
      setDeleteConfirmOpen(false);
      setRoleToDelete(null);
      // Refetch the roles list to update the UI
      refetchRoles();
    } catch (error: any) {
      console.error("Error deleting role:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle assign permissions
  const handleAssignPermissions = (role: any) => {
    setSelectedRoleForPermissions(role);
    setPermissionsModalOpen(true);
  };

  // Handle permissions modal close
  const handlePermissionsModalClose = () => {
    setPermissionsModalOpen(false);
    setSelectedRoleForPermissions(null);
  };

  // Handle permissions update success
  const handlePermissionsUpdateSuccess = () => {
    refetchRoles(); // Refresh the roles list
  };

  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 1,
      minWidth: 20,
      flex: 0,
      renderCell: () => <></>,
      cellClassName: "no-padding-cell",
      headerClassName: "no-padding-cell",
    },
    {
      field: "id",
      headerName: t("roles.list.columns.id"),
      width: 100,
      sortable: true,
    },
    {
      field: "role_name",
      headerName: t("roles.list.columns.roleName"),
      width: 100,
      flex: 1,
      sortable: true,
      renderCell: (params: any) => (
        <LimitChractersWithTooltip value={params.value || ""} />
      ),
    },
    {
      field: "role_description",
      headerName: t("roles.list.columns.description"),
      flex: 1,
      sortable: true,
      renderCell: (params: any) => (
        <LimitChractersWithTooltip value={params.value || ""} />
      ),
    },
    {
      field: "modified_by_user",
      headerName: t("roles.list.columns.modifiedBy"),
      minWidth: 140,
      flex: 1,
      sortable: true,
      renderCell: (params: any) => {
        return <LimitChractersWithTooltip value={params?.value?.username} />;
      },
    },
    {
      field: "updated_on",
      headerName: t("roles.list.columns.updatedOn"),
      minWidth: 180,
      flex: 1,
      sortable: true,
      renderCell: (params: any) => {
        return getFormattedDateTime(params.value);
      },
    },
    {
      field: "is_active",
      headerName: t("roles.list.columns.status"),
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) => {
        return params.value ? (
          <Tooltip title={t("roles.list.tooltip.active")} placement="top" arrow>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCheckCircleIconGreenSvg width={21} height={21} />
            </Box>
          </Tooltip>
        ) : (
          <Tooltip
            title={t("roles.list.tooltip.inactive")}
            placement="top"
            arrow
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCrossCircleIconSvg width={21} height={21} />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      headerName: t("roles.list.columns.actions"),
      align: "center",
      headerAlign: "center",
      width: 200,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => {
        const isRoleActive = params.row.is_active === true;

        return (
          <>
            {/* For active records, show all action buttons */}
            {isRoleActive && (
              <>
                {canViewRole && (
                  <Tooltip
                    title={t("roles.list.tooltip.view")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => navigate(`/roles/view/${params.row.id}`)}
                    >
                      <IconEyeBase />
                    </IconButton>
                  </Tooltip>
                )}
                {canEditRole && (
                  <Tooltip
                    title={t("roles.list.tooltip.edit")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => navigate(`/roles/edit/${params.row.id}`)}
                    >
                      <IconEditBase />
                    </IconButton>
                  </Tooltip>
                )}
                {canAssignPermissions && !isAdminRole(params.row) && (
                  <Tooltip
                    title={t("roles.list.tooltip.assignPermissions")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => handleAssignPermissions(params.row)}
                    >
                      <IconAssignPermissionsBase />
                    </IconButton>
                  </Tooltip>
                )}
                {canAssignPermissions && isAdminRole(params.row) && (
                  <Tooltip
                    title={t("roles.list.tooltip.adminPermissionsDisabled")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      disabled
                      sx={{ opacity: 0.5 }}
                    >
                      <IconAssignPermissionsBase />
                    </IconButton>
                  </Tooltip>
                )}
                {canDeleteRole && !isAdminRole(params.row) && (
                  <Tooltip
                    title={t("roles.list.tooltip.delete")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => handleDeleteRole(params.row)}
                    >
                      <IconDeleteBlueSvg />
                    </IconButton>
                  </Tooltip>
                )}
                {canDeleteRole && isAdminRole(params.row) && (
                  <Tooltip
                    title={t("roles.list.tooltip.adminDeleteDisabled")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      disabled
                      sx={{ opacity: 0.5 }}
                    >
                      <IconDeleteBlueSvg />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}

            {/* For inactive records, show ONLY Reactivate button */}
            {!isRoleActive && canEditRole && (
              <Tooltip
                title={t("roles.list.tooltip.reactivate")}
                placement="top"
                arrow
              >
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() => handleReactivate(params.row)}
                >
                  <IconReactivateSvg />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  return (
    <Box>
      <Loader isLoading={isLoading} />

      <ConfirmDailog
        isDailogOpen={deleteConfirmOpen}
        handleCancelAction={() => setDeleteConfirmOpen(false)}
        handleConfirmAction={confirmDelete}
        dailogTitle={t("roles.dialog.deleteTitle")}
        dailogDescription={t("roles.dialog.deleteDescription", {
          name: roleToDelete?.role_name,
        })}
      />

      {selectedRoleForPermissions && (
        <AssignPermissionsModal
          open={permissionsModalOpen}
          onClose={handlePermissionsModalClose}
          roleId={selectedRoleForPermissions.id}
          roleName={selectedRoleForPermissions.role_name}
          roleType={selectedRoleForPermissions.role_type}
          onSuccess={handlePermissionsUpdateSuccess}
        />
      )}

      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container rowSpacing={2.5} columnSpacing={2}>
          <Grid item xs>
            <SearchFilterBar
              largeWidth={true}
              searchFilterData={searchFilterData}
              setSearchFilterData={setSearchFilterData}
              requiredFields={["role_name", "status_filter"]}
              primaryFields={["role_name", "status_filter"]}
              fieldRequirements={{
                defaultStatusFilter: "Active", // Reset to Active status on Clear Filter
              }}
            />
          </Grid>
          {canAddRole && (
            <Grid
              item
              xs="auto"
              sx={{ display: "flex", alignItems: "flex-start" }}
            >
              <ButtonComponent
                handelClickEvent={() => navigate("/roles/add")}
                className="btn-orange"
              >
                <>
                  <AddSharpIcon sx={{ marginRight: "4px" }} />{" "}
                  {t("roles.list.title")}
                </>
              </ButtonComponent>
            </Grid>
          )}
        </Grid>
      </Box>

      <FlexBetween>
        <DataTable
          dataColumns={columns || []}
          dataRows={rolesData?.items || []}
          loading={isLoading}
          dataListTitle={t("roles.list.dataListTitle")}
          className="dataTable no-radius hide-progress-icon"
          paginationMode="server"
          rowCount={rolesData?.total || 0}
          rowGroupByColumnProps={enableRowGroupByColumnProps}
          paginationModel={{
            page: page - 1,
            pageSize: pSize,
          }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          fitViewport
          singlePageMaxHeightDiff={358}
        />
      </FlexBetween>
    </Box>
  );
};

export default Roles;
